import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Layout } from './components/Layout/Layout';
import { AdminSidebar } from './components/Admin/AdminSidebar';
import { ScrollToTop } from './components/ScrollToTop';
import { ConnectionStatus } from './components/ui/ConnectionStatus';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { Toaster } from 'sonner';

// Critical pages - load immediately (Home, Auth, Landing)
import { Home } from './pages/Home';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import { Landing } from './pages/Landing';

// Lazy load heavy pages
const EventsPage = lazy(() => import('./pages/Events').then(m => ({ default: m.EventsPage })));
const Thanks = lazy(() => import('./pages/Thanks'));
const Naboen = lazy(() => import('./pages/Naboen'));
const Bofellesskapet = lazy(() => import('./pages/Bofellesskapet'));
const Reservasjoner = lazy(() => import('./pages/Reservasjoner'));
const FAQ = lazy(() => import('./pages/Faq'));
const Kontakt = lazy(() => import('./pages/Kontakt'));
const ReportProblem = lazy(() => import('./pages/ReportProblem'));
const Personvern = lazy(() => import('./pages/Personvern').then(m => ({ default: m.Personvern })));
const Produktutvikling = lazy(() => import('./pages/Produktutvikling'));
const WeatherPage = lazy(() => import('./pages/WeatherPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profil = lazy(() => import('./pages/Profil').then(m => ({ default: m.Profil })));
const EmailNotificationSettings = lazy(() => import('./pages/EmailNotificationSettings').then(m => ({ default: m.EmailNotificationSettings })));
const OmOss = lazy(() => import('./pages/OmOss').then(m => ({ default: m.OmOss })));
const MinCondo = lazy(() => import('./pages/MinCondo'));
const Invite = lazy(() => import('./pages/Invite').then(m => ({ default: m.Invite })));
const Vilkar = lazy(() => import('./pages/Vilkar').then(m => ({ default: m.Vilkar })));
const Blogg = lazy(() => import('./pages/Blogg').then(m => ({ default: m.Blogg })));
const BloggArtikkel = lazy(() => import('./pages/BloggArtikkel').then(m => ({ default: m.BloggArtikkel })));

// Admin pages - lazy load (heavy)
const AdminBofellesskap = lazy(() => import('./pages/Admin/Bofellesskap'));
const ResidenceDetails = lazy(() => import('./pages/Admin/ResidenceDetails'));
const Brukere = lazy(() => import('./pages/Admin/Brukere'));
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard').then(m => ({ default: m.Dashboard })));
const CreateResidence = lazy(() => import('./pages/Admin/CreateResidence'));

// Styret pages - lazy load (heavy)
const Styret = lazy(() => import('./pages/Styret').then(m => ({ default: m.Styret })));
const StyretSidebar = lazy(() => import('./components/Styret/StyretSidebar').then(m => ({ default: m.StyretSidebar })));
const Bofellesskap = lazy(() => import('./pages/Styret/Bofellesskap').then(m => ({ default: m.Bofellesskap })));
const Innstillinger = lazy(() => import('./pages/Styret/Innstillinger').then(m => ({ default: m.Innstillinger })));
const Beboere = lazy(() => import('./pages/Styret/Beboere').then(m => ({ default: m.Beboere })));
const Boliger = lazy(() => import('./pages/Styret/Boliger').then(m => ({ default: m.Boliger })));
const Oversikt = lazy(() => import('./pages/Styret/Oversikt').then(m => ({ default: m.Oversikt })));
const Dokumenter = lazy(() => import('./pages/Styret/Dokumenter').then(m => ({ default: m.Dokumenter })));
const StyretReservasjoner = lazy(() => import('./pages/Styret/Reservasjoner').then(m => ({ default: m.Reservasjoner })));

export function App() {
  // Show connection status if Supabase URL is not set
  const showConnectionStatus = !import.meta.env.VITE_SUPABASE_URL;

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <UserProvider>
            <ScrollToTop />
            <RoutedApp />
            {showConnectionStatus && <ConnectionStatus />}
<Toaster position="top-center" richColors />
          <SpeedInsights />
        </UserProvider>
      </AuthProvider>
    </BrowserRouter>
  </HelmetProvider>
  );
}

// Loading fallback component
function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function RoutedApp() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        {/* Public pages - always accessible, no auth check */}
        <Route path="/landing" element={<Landing />} />
        <Route path="/vilkar" element={<Suspense fallback={<RouteLoadingFallback />}><Vilkar /></Suspense>} />
        <Route path="/blogg" element={<Suspense fallback={<RouteLoadingFallback />}><Blogg /></Suspense>} />
        <Route path="/blogg/:slug" element={<Suspense fallback={<RouteLoadingFallback />}><BloggArtikkel /></Suspense>} />
        
        {/* Auth routes - ProtectedRoute håndterer redirect hvis allerede innlogget */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<Suspense fallback={<RouteLoadingFallback />}><ForgotPassword /></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<RouteLoadingFallback />}><ResetPassword /></Suspense>} />
        <Route path="/invite/:token" element={<Suspense fallback={<RouteLoadingFallback />}><Invite /></Suspense>} />
        
        {/* Protected Routes - ProtectedRoute håndterer auth-sjekk */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Home />} />
          <Route path="naboen" element={<Suspense fallback={<RouteLoadingFallback />}><Naboen /></Suspense>} />
          <Route path="faq" element={<Suspense fallback={<RouteLoadingFallback />}><FAQ /></Suspense>} />
          <Route path="kontakt" element={<Suspense fallback={<RouteLoadingFallback />}><Kontakt /></Suspense>} />
          <Route path="rapporter" element={<Suspense fallback={<RouteLoadingFallback />}><ReportProblem /></Suspense>} />
          <Route path="personvern" element={<Suspense fallback={<RouteLoadingFallback />}><Personvern /></Suspense>} />
          <Route path="produktutvikling" element={<Suspense fallback={<RouteLoadingFallback />}><Produktutvikling /></Suspense>} />
          <Route path="vaer" element={<Suspense fallback={<RouteLoadingFallback />}><WeatherPage /></Suspense>} />
          <Route path="om-oss" element={<Suspense fallback={<RouteLoadingFallback />}><OmOss /></Suspense>} />
          <Route path="takk" element={<Suspense fallback={<RouteLoadingFallback />}><Thanks /></Suspense>} />
          <Route path="events" element={<Suspense fallback={<RouteLoadingFallback />}><EventsPage /></Suspense>} />
          <Route path="profil" element={<Suspense fallback={<RouteLoadingFallback />}><Profil /></Suspense>} />
          <Route path="epost-varslinger" element={<Suspense fallback={<RouteLoadingFallback />}><EmailNotificationSettings /></Suspense>} />
        </Route>

        {/* Residence-specific routes */}
        <Route path="/min-bolig" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Suspense fallback={<RouteLoadingFallback />}><MinCondo /></Suspense>} />
        </Route>
        
        <Route path="/bofellesskapet" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Suspense fallback={<RouteLoadingFallback />}><Bofellesskapet /></Suspense>} />
        </Route>
        
        <Route path="/reservasjoner" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Suspense fallback={<RouteLoadingFallback />}><Reservasjoner /></Suspense>} />
        </Route>

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <Layout showDesktopSidebar desktopSidebar={<AdminSidebar />} />
          </ProtectedRoute>
        }>
          <Route index element={<Suspense fallback={<RouteLoadingFallback />}><AdminDashboard /></Suspense>} />
          <Route path="bofellesskap" element={<Suspense fallback={<RouteLoadingFallback />}><AdminBofellesskap /></Suspense>} />
          <Route path="bofellesskap/opprett" element={<Suspense fallback={<RouteLoadingFallback />}><CreateResidence /></Suspense>} />
          <Route path="bofellesskap/:id" element={<Suspense fallback={<RouteLoadingFallback />}><ResidenceDetails /></Suspense>} />
          <Route path="brukere" element={<Suspense fallback={<RouteLoadingFallback />}><Brukere /></Suspense>} />
          <Route path="meldinger" element={<div>Meldinger Admin</div>} />
          <Route path="innstillinger" element={<div>Innstillinger Admin</div>} />
        </Route>

        <Route path="/styret" element={
          <ProtectedRoute boardOnly>
            <Layout showDesktopSidebar desktopSidebar={
              <Suspense fallback={<div className="p-4"><LoadingSpinner /></div>}>
                <StyretSidebar />
              </Suspense>
            } />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="oversikt" replace />} />
          <Route path="oversikt" element={<Suspense fallback={<RouteLoadingFallback />}><Oversikt /></Suspense>} />
          <Route path="styret" element={<Suspense fallback={<RouteLoadingFallback />}><Styret /></Suspense>} />
          <Route path="bofellesskap/:id" element={<Suspense fallback={<RouteLoadingFallback />}><Bofellesskap /></Suspense>} />
          <Route path="beboere/:id" element={<Suspense fallback={<RouteLoadingFallback />}><Beboere /></Suspense>} />
          <Route path="boliger/:id" element={<Suspense fallback={<RouteLoadingFallback />}><Boliger /></Suspense>} />
          <Route path="reservasjoner" element={<Suspense fallback={<RouteLoadingFallback />}><StyretReservasjoner /></Suspense>} />
          <Route path="dokumenter" element={<Suspense fallback={<RouteLoadingFallback />}><Dokumenter /></Suspense>} />
          <Route path="innstillinger" element={<Suspense fallback={<RouteLoadingFallback />}><Innstillinger /></Suspense>} />
        </Route>

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
